import { useState } from 'react'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useAuth } from '../../context/AuthContext'
import Button from '../../components/ui/Button'
import { fadeInUp } from '../../utils/animations'
import { getAdminToken, getRememberedUsername, getRememberMePreference } from '../../utils/adminAuth'
import { ADMIN_DASHBOARD_PATH } from '../../constants/adminRoutes'

export default function AdminLogin() {
  const { t } = useTranslation()
  const { login, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [form, setForm] = useState(() => ({
    username: getRememberedUsername(),
    password: '',
  }))
  const [rememberMe, setRememberMe] = useState(getRememberMePreference)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (isAuthenticated || getAdminToken()) {
    return <Navigate to={ADMIN_DASHBOARD_PATH} replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(form, rememberMe)
      const from = location.state?.from?.pathname || ADMIN_DASHBOARD_PATH
      navigate(from, { replace: true })
    } catch {
      setError(t('admin.login.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <img
          src="/images/admin-login-bg.png"
          alt=""
          className="h-full w-full object-cover blur-sm"
        />
        <div className="absolute inset-0 bg-white/25" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="relative w-full max-w-md rounded-2xl border border-white/80 bg-white/95 p-8 shadow-xl shadow-primary/15 backdrop-blur-sm"
      >
        <div className="mb-8 text-center">
          <img
            src="/logo/logo-color.png"
            alt={t('brand.name')}
            className="mx-auto mb-6 h-20 w-auto object-contain"
          />
          <h1 className="text-2xl font-bold text-primary">{t('admin.login.title')}</h1>
          <p className="mt-2 text-sm text-text/60">{t('admin.login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="username" className="mb-1.5 block text-sm font-medium text-text">
              {t('admin.login.username')}
            </label>
            <input
              id="username"
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full rounded-xl border border-border px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              autoComplete="username"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-text">
              {t('admin.login.password')}
            </label>
            <input
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-xl border border-border px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              autoComplete="current-password"
              required
            />
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-text/80">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            {t('admin.login.rememberMe')}
          </label>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <Button type="submit" variant="primary" className="w-full" disabled={loading}>
            {loading ? t('common.loading') : t('admin.login.submit')}
          </Button>
        </form>
      </motion.div>
    </div>
  )
}
